import { useEffect, useMemo, useRef, useState } from "react";
import { groupOperatingHours } from "@/lib/operatingHours";
import {
  isLunchMenuCategory,
  isProductOrderableNow,
  isWeekday,
  kitchenCloseMinutesForBusiness,
} from "@/lib/menuAvailability";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarCheck, Check, CheckCircle2, ChevronDown, Clock, ExternalLink, MapPin, Menu, Minus, Plus, ShoppingBag, Trash2, Truck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useProjectLocal } from "@/context/AppContext";
import { LoadingScreen, Money } from "@/components/project/Primitives";
import { formatMoney } from "@/lib/currency";
import { activeDeliveryPlatforms } from "@/lib/deliveryPlatforms";
import { activeLegalDocuments, extractLegalUpdatedLabel, formatLegalHtml, formatLegalUpdatedDate, formatBusinessAddress, legalDocumentLabel } from "@/lib/legalDocuments";
import { resolveStoreTheme } from "@/lib/storeTheme";
import { defaultProductImageUrl, resolveProductImageUrl } from "@/lib/productMedia";
import { apiGet, apiPost, BACKEND_URL } from "@/api/client";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { toast } from "@/components/ui/sonner";
import { normalizeEnabledLanguages, normalizeStoreLanguage } from "@/lib/languages";
import { isCustomDomainHost, storePath } from "@/lib/customDomain";
import { DomainNotConnected } from "@/pages/DomainNotConnected";

function resolveStoreMedia(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL || ""}${url}`;
}

/** Best-effort display name before storefront JSON arrives (URL slug or preview tenant only). */
function humanizeSlug(slug) {
  const value = String(slug || "").trim();
  if (!value) return "";
  return value
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Loading copy must stay stable. Do not pull names from bootstrap/app context on public
 * or custom-domain visits — that briefly flashes the logged-in/demo merchant (e.g. Sample)
 * before the real storefront loads.
 */
function storefrontLoadingName({ routeSlug, previewBusiness }) {
  if (routeSlug) return humanizeSlug(routeSlug);
  if (previewBusiness?.name) return String(previewBusiness.name).trim();
  return "";
}

/** Header / footer brand: uploaded logo when present, otherwise business name. */
function StoreBrandLink({
  business,
  to,
  testId = "storefront-brand-link",
  nameTestId = "storefront-business-name",
  logoTestId = "storefront-logo-image",
  className = "text-xl font-extrabold",
  logoClassName = "h-16 w-auto max-h-16 max-w-[240px] object-contain",
}) {
  const logoSrc = resolveStoreMedia(business?.logo_url);
  return (
    <Link
      to={to || storePath(business.slug)}
      className={`flex min-w-0 items-center gap-2.5 ${className}`}
      data-testid={testId}
      aria-label={business.name}
    >
      {logoSrc ? (
        <img src={logoSrc} alt={business.name} className={logoClassName} data-testid={logoTestId} decoding="async" />
      ) : (
        <span className="truncate" data-testid={nameTestId}>{business.name}</span>
      )}
    </Link>
  );
}

const HOME_MENU_PREVIEW_LIMIT = 6;

const i18n = {
  en: { menu: "Menu", about: "About", hours: "Hours", cart: "Cart", open: "Open now", closed: "Temporarily closed", pickup: "Pickup", delivery: "delivery", start: "Start an order", paused: "Ordering paused", liveFeature: "Live feature", orderNow: "Order now", fullMenu: "Full menu", menuPreview: "A few favorites from the kitchen — open the full menu for every item.", menuVisual: "Browse every dish by category, with dietary filters.", menuCompact: "The complete menu — fast to scan and easy to order.", story: "Our story", location: "Location", add: "Add", addToCart: "Add to cart", soldOut: "Sold out", checkout: "Checkout", back: "← Back to store", orderReview: "Order review", placeOrder: "Place order", notes: "Special instructions", name: "Name", email: "Email", phone: "Phone", deliveryAddress: "Delivery address", city: "City", postcode: "Postcode", total: "Total", emptyCart: "Your cart is empty.", confirmed: "Order confirmed", confirmedBody: "Your order has been received. Save this order number and wait for the business to confirm your ready time." , reserve: "Reserve", reserveTitle: "Reserve a table", reserveBody: "Request a table for your next visit — we'll confirm by email or WhatsApp.", reserveCopyTitle: "Join us for dinner", reserveCopyBody: "Tell us when you'd like to come in and how many are joining. We'll confirm your reservation shortly — nothing is charged for booking a table.", reserveGuests: "{min}–{max} guests", reserveNotice: "{hours}hr min notice", reserveNoticeSameDay: "Same-day ok", reserveDate: "Date", reserveTime: "Time", reserveParty: "Party size", reserveGuestOne: "{n} guest", reserveGuestMany: "{n} guests", reservePhonePlaceholder: "+49 ...", reserveNamePlaceholder: "Your name", reserveEmailPlaceholder: "you@email.com", reserveSubmit: "Request reservation", reserveSending: "Sending…", reservePreview: "Preview only", reserveNote: "We'll confirm within a few hours. No payment required.", reserveHoldNote: "We hold your table for 10 minutes past your reservation time — after that, we may release it to walk-ins.", reserveSuccessTitle: "Request received", reserveSuccessConfirm: "We'll confirm by email at {email}.", reserveToastOk: "Reservation requested — we'll confirm shortly.", reserveToastFail: "Could not request reservation.", reservePreviewToast: "Reservations are disabled in preview mode." , gallery: "Gallery", galleryBody: "A look inside {name}." , all: "All" , reviewsKicker: "Google reviews", reviewsTitle: "Loved by local customers", showMore: "Show more", showLess: "Show less", hoursClosed: "Closed", hoursSoon: "Hours coming soon", deliveryMeta: "{time} · {fee} fee · {radius} {unit} radius", mile: "mile", km: "km", dayMon: "Mon", dayTue: "Tue", dayWed: "Wed", dayThu: "Thu", dayFri: "Fri", daySat: "Sat", daySun: "Sun" , footerContact: "Contact", footerLegal: "Legal" , home: "Home", notAvailable: "Not available", lunchBadgeShort: "Mon–Fri only", lunchBadgeLong: "Mon–Fri only", lunchWeekendBanner: "The lunch menu is only available Monday to Friday.", kitchenCloseNotice: "The kitchen closes 10 minutes before closing. Orders aren't accepted during this time." },
  de: { menu: "Speisekarte", about: "Über uns", hours: "Zeiten", cart: "Warenkorb", open: "Jetzt geöffnet", closed: "Vorübergehend geschlossen", pickup: "Abholung", delivery: "Lieferung", start: "Bestellung starten", paused: "Bestellung pausiert", liveFeature: "Aktuelles Angebot", orderNow: "Jetzt bestellen", fullMenu: "Ganze Karte", menuPreview: "Einige Favoriten aus der Küche — die ganze Karte zeigt alle Gerichte.", menuVisual: "Alle Gerichte nach Kategorie, mit Ernährungsfiltern.", menuCompact: "Die komplette Speisekarte — schnell und übersichtlich.", story: "Unsere Geschichte", location: "Standort", add: "Hinzufügen", addToCart: "In den Warenkorb", soldOut: "Ausverkauft", checkout: "Kasse", back: "← Zurück zum Shop", orderReview: "Bestellübersicht", placeOrder: "Bestellen", notes: "Besondere Hinweise", name: "Name", email: "E-Mail", phone: "Telefon", deliveryAddress: "Lieferadresse", city: "Stadt", postcode: "PLZ", total: "Summe", emptyCart: "Ihr Warenkorb ist leer.", confirmed: "Bestellung bestätigt", confirmedBody: "Ihre Bestellung ist eingegangen. Bitte speichern Sie diese Bestellnummer." , reserve: "Reservieren", reserveTitle: "Tisch reservieren", reserveBody: "Reservieren Sie einen Tisch für Ihren nächsten Besuch — wir bestätigen per E-Mail oder WhatsApp.", reserveCopyTitle: "Kommen Sie zum Essen vorbei", reserveCopyBody: "Teilen Sie uns mit, wann Sie kommen möchten und wie viele Personen dabei sind. Wir bestätigen Ihre Reservierung in Kürze — die Tischreservierung ist kostenlos.", reserveGuests: "{min}–{max} Gäste", reserveNotice: "Mindestens {hours} Std. im Voraus", reserveNoticeSameDay: "Auch am selben Tag möglich", reserveDate: "Datum", reserveTime: "Uhrzeit", reserveParty: "Personenzahl", reserveGuestOne: "{n} Gast", reserveGuestMany: "{n} Gäste", reservePhonePlaceholder: "+49 …", reserveNamePlaceholder: "Ihr Name", reserveEmailPlaceholder: "sie@email.com", reserveSubmit: "Reservierung anfragen", reserveSending: "Wird gesendet…", reservePreview: "Nur Vorschau", reserveNote: "Wir bestätigen innerhalb weniger Stunden. Keine Zahlung erforderlich.", reserveHoldNote: "Wir halten Ihren Tisch 10 Minuten über die Reservierungszeit hinaus frei — danach kann er an Laufkundschaft vergeben werden.", reserveSuccessTitle: "Anfrage erhalten", reserveSuccessConfirm: "Wir bestätigen per E-Mail an {email}.", reserveToastOk: "Reservierung angefragt — wir bestätigen in Kürze.", reserveToastFail: "Reservierung konnte nicht angefragt werden.", reservePreviewToast: "Reservierungen sind im Vorschaumodus deaktiviert." , gallery: "Galerie", galleryBody: "Ein Blick in {name}." , all: "Alle" , reviewsKicker: "Google-Bewertungen", reviewsTitle: "Beliebt bei Gästen aus der Nachbarschaft", showMore: "Mehr anzeigen", showLess: "Weniger anzeigen", hoursClosed: "Geschlossen", hoursSoon: "Öffnungszeiten folgen", deliveryMeta: "{time} · {fee} Gebühr · {radius} {unit} Umkreis", mile: "Meile", km: "km", dayMon: "Mo", dayTue: "Di", dayWed: "Mi", dayThu: "Do", dayFri: "Fr", daySat: "Sa", daySun: "So" , footerContact: "Kontakt", footerLegal: "Rechtliches" , home: "Start", notAvailable: "Nicht mehr verfügbar", lunchBadgeShort: "Nur Mo–Fr", lunchBadgeLong: "Nur Montag bis Freitag", lunchWeekendBanner: "Die Mittagskarte ist nur von Montag bis Freitag verfügbar.", kitchenCloseNotice: "Die Küche schließt 10 Minuten vor Ladenschluss. Für diesen Zeitraum sind keine Bestellungen mehr möglich." },
  es: { menu: "Menú", about: "Sobre nosotros", hours: "Horario", cart: "Carrito", open: "Abierto ahora", closed: "Cerrado temporalmente", pickup: "Recogida", delivery: "entrega", start: "Empezar pedido", paused: "Pedidos pausados", liveFeature: "Destacado", orderNow: "Pedir ahora", fullMenu: "Menú completo", menuPreview: "Algunos favoritos de la cocina — abre el menú completo para verlo todo.", menuVisual: "Todos los platos por categoría, con filtros dietéticos.", menuCompact: "El menú completo — rápido de revisar y fácil de pedir.", story: "Nuestra historia", location: "Ubicación", add: "Añadir", addToCart: "Añadir al carrito", soldOut: "Agotado", checkout: "Pago", back: "← Volver a la tienda", orderReview: "Resumen del pedido", placeOrder: "Realizar pedido", notes: "Instrucciones especiales", name: "Nombre", email: "Email", phone: "Teléfono", deliveryAddress: "Dirección de entrega", city: "Ciudad", postcode: "Código postal", total: "Total", emptyCart: "Tu carrito está vacío.", confirmed: "Pedido confirmado", confirmedBody: "Hemos recibido tu pedido. Guarda este número de pedido." , reserve: "Reservar", reserveTitle: "Reservar una mesa", reserveBody: "Solicita una mesa para tu próxima visita — confirmaremos por email o WhatsApp.", reserveCopyTitle: "Ven a comer con nosotros", reserveCopyBody: "Dinos cuándo quieres venir y cuántas personas. Confirmaremos tu reserva en breve — reservar mesa es gratis.", reserveGuests: "{min}–{max} comensales", reserveNotice: "Aviso mínimo {hours} h", reserveNoticeSameDay: "Mismo día ok", reserveDate: "Fecha", reserveTime: "Hora", reserveParty: "Comensales", reserveGuestOne: "{n} comensal", reserveGuestMany: "{n} comensales", reservePhonePlaceholder: "+49 ...", reserveNamePlaceholder: "Tu nombre", reserveEmailPlaceholder: "tu@email.com", reserveSubmit: "Solicitar reserva", reserveSending: "Enviando…", reservePreview: "Solo vista previa", reserveNote: "Confirmamos en pocas horas. No se requiere pago.", reserveHoldNote: "Guardamos tu mesa 10 minutos después de la hora reservada — después puede liberarse para walk-ins.", reserveSuccessTitle: "Solicitud recibida", reserveSuccessConfirm: "Confirmaremos por email a {email}.", reserveToastOk: "Reserva solicitada — confirmaremos en breve.", reserveToastFail: "No se pudo solicitar la reserva.", reservePreviewToast: "Las reservas están desactivadas en modo vista previa." , gallery: "Galería", galleryBody: "Una mirada dentro de {name}." , all: "Todos" , reviewsKicker: "Reseñas de Google", reviewsTitle: "Queridos por los clientes locales", showMore: "Ver más", showLess: "Ver menos", hoursClosed: "Cerrado", hoursSoon: "Horario próximamente", deliveryMeta: "{time} · tarifa {fee} · radio de {radius} {unit}", mile: "milla", km: "km", dayMon: "lun", dayTue: "mar", dayWed: "mié", dayThu: "jue", dayFri: "vie", daySat: "sáb", daySun: "dom" , footerContact: "Contacto", footerLegal: "Legal" , home: "Inicio", notAvailable: "Not available", lunchBadgeShort: "Mon–Fri only", lunchBadgeLong: "Mon–Fri only", lunchWeekendBanner: "The lunch menu is only available Monday to Friday.", kitchenCloseNotice: "The kitchen closes 10 minutes before closing. Orders aren't accepted during this time." },
  pt: { menu: "Menu", about: "Sobre nós", hours: "Horário", cart: "Carrinho", open: "Aberto agora", closed: "Fechado temporariamente", pickup: "Recolha", delivery: "entrega", start: "Iniciar pedido", paused: "Pedidos pausados", liveFeature: "Destaque", orderNow: "Pedir agora", fullMenu: "Menu completo", menuPreview: "Alguns favoritos da cozinha — abra o menu completo para ver tudo.", menuVisual: "Todos os pratos por categoria, com filtros alimentares.", menuCompact: "O menu completo — fácil de consultar e pedir.", story: "A nossa história", location: "Localização", add: "Adicionar", addToCart: "Adicionar ao carrinho", soldOut: "Esgotado", checkout: "Checkout", back: "← Voltar à loja", orderReview: "Resumo do pedido", placeOrder: "Fazer pedido", notes: "Instruções especiais", name: "Nome", email: "Email", phone: "Telefone", deliveryAddress: "Endereço de entrega", city: "Cidade", postcode: "Código postal", total: "Total", emptyCart: "O carrinho está vazio.", confirmed: "Pedido confirmado", confirmedBody: "Recebemos o seu pedido. Guarde este número." , reserve: "Reservar", reserveTitle: "Reservar uma mesa", reserveBody: "Peça uma mesa para a sua próxima visita — confirmamos por email ou WhatsApp.", reserveCopyTitle: "Venha jantar connosco", reserveCopyBody: "Diga-nos quando quer vir e quantas pessoas. Confirmamos a sua reserva em breve — reservar mesa é gratuito.", reserveGuests: "{min}–{max} pessoas", reserveNotice: "Aviso mínimo {hours} h", reserveNoticeSameDay: "No mesmo dia ok", reserveDate: "Data", reserveTime: "Hora", reserveParty: "Número de pessoas", reserveGuestOne: "{n} pessoa", reserveGuestMany: "{n} pessoas", reservePhonePlaceholder: "+49 ...", reserveNamePlaceholder: "O seu nome", reserveEmailPlaceholder: "voce@email.com", reserveSubmit: "Pedir reserva", reserveSending: "A enviar…", reservePreview: "Só pré-visualização", reserveNote: "Confirmamos em poucas horas. Não é necessário pagamento.", reserveHoldNote: "Guardamos a mesa 10 minutos além da hora da reserva — depois pode ser libertada para clientes sem reserva.", reserveSuccessTitle: "Pedido recebido", reserveSuccessConfirm: "Confirmaremos por email para {email}.", reserveToastOk: "Reserva pedida — confirmamos em breve.", reserveToastFail: "Não foi possível pedir a reserva.", reservePreviewToast: "As reservas estão desativadas no modo de pré-visualização." , gallery: "Galeria", galleryBody: "Um olhar por dentro de {name}." , all: "Todos" , reviewsKicker: "Avaliações do Google", reviewsTitle: "Adorado pelos clientes locais", showMore: "Ver mais", showLess: "Ver menos", hoursClosed: "Fechado", hoursSoon: "Horário em breve", deliveryMeta: "{time} · taxa {fee} · raio de {radius} {unit}", mile: "milha", km: "km", dayMon: "seg", dayTue: "ter", dayWed: "qua", dayThu: "qui", dayFri: "sex", daySat: "sáb", daySun: "dom" , footerContact: "Contacto", footerLegal: "Legal" , home: "Início", notAvailable: "Not available", lunchBadgeShort: "Mon–Fri only", lunchBadgeLong: "Mon–Fri only", lunchWeekendBanner: "The lunch menu is only available Monday to Friday.", kitchenCloseNotice: "The kitchen closes 10 minutes before closing. Orders aren't accepted during this time." }
};
const languageOptions = [["en", "EN"], ["de", "DE"], ["es", "ES"], ["pt", "PT"]];

function storefrontLanguageOptions(business) {
  const enabled = normalizeEnabledLanguages(business?.enabled_languages, business?.default_language || "en");
  return languageOptions.filter(([code]) => enabled.includes(code));
}

export function Storefront({ view = "home" }) {
  const params = useParams();
  const location = useLocation();
  const { data: appData, cart, addToCart, updateCartQuantity, removeFromCart, clearCart, lastOrder, setLastOrder } = useProjectLocal();
  const customHost = isCustomDomainHost();
  const isPreviewRoute = !customHost && location.pathname === "/preview";
  const [hostSlug, setHostSlug] = useState(null);
  const [hostFailed, setHostFailed] = useState(false);
  // Never fall back to bootstrap/demo business slug on public or custom-domain storefronts —
  // that re-triggers fetch and flashes the wrong loading name (Sample → real store).
  const slug = params.slug || hostSlug || (isPreviewRoute ? appData?.business?.slug : null);
  const navigate = useNavigate();
  const [storeData, setStoreData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [storeError, setStoreError] = useState(null);
  const [language, setLanguage] = useState(() => window.localStorage.getItem("project_local_storefront_language") || "");
  const defaultLanguage = normalizeStoreLanguage(storeData?.business?.default_language || "en");
  const enabledLanguages = normalizeEnabledLanguages(storeData?.business?.enabled_languages, defaultLanguage);
  const requestedLanguage = language || storeData?.business?.active_language || defaultLanguage;
  const activeLanguage = enabledLanguages.includes(normalizeStoreLanguage(requestedLanguage))
    ? normalizeStoreLanguage(requestedLanguage)
    : defaultLanguage;
  const t = i18n[activeLanguage] || i18n.en;
  const setStoreLanguage = (lang) => {
    const next = normalizeStoreLanguage(lang);
    if (!enabledLanguages.includes(next)) return;
    setLanguage(next);
    window.localStorage.setItem("project_local_storefront_language", next);
  };
  const loadingIdentity = params.slug || (customHost ? `host:${typeof window !== "undefined" ? window.location.hostname : ""}` : isPreviewRoute ? "preview" : "");
  const lockedLoadingNameRef = useRef({ identity: loadingIdentity, name: "" });
  if (lockedLoadingNameRef.current.identity !== loadingIdentity) {
    lockedLoadingNameRef.current = { identity: loadingIdentity, name: "" };
  }
  const loadingNameCandidate = storefrontLoadingName({
    routeSlug: params.slug || null,
    previewBusiness: isPreviewRoute ? appData?.business : null,
  });
  if (loadingNameCandidate && !lockedLoadingNameRef.current.name) {
    lockedLoadingNameRef.current.name = loadingNameCandidate;
  }
  const loadingName = lockedLoadingNameRef.current.name || loadingNameCandidate;
  useEffect(() => {
    setNotFound(false);
    setStoreError(null);
    setHostFailed(false);
    const langQuery = language ? `?lang=${language}` : "";

    // Custom domain at / (no slug in URL): resolve tenant by browser hostname.
    if (customHost && !params.slug) {
      const domainQuery = `domain=${encodeURIComponent(window.location.hostname)}`;
      const langPart = language ? `lang=${encodeURIComponent(language)}` : "";
      const qs = langPart ? `?${domainQuery}&${langPart}` : `?${domainQuery}`;
      apiGet(`/storefront/by-host${qs}`)
        .then((payload) => {
          setHostSlug(payload.business?.slug || null);
          setStoreData(payload);
          trackAnalyticsEvent({
            event_type: "page_view",
            slug: payload.business?.slug,
            path: window.location.pathname || "/",
          });
        })
        .catch((error) => {
          const detail = error.response?.data?.detail;
          const code = detail?.error?.code || detail?.code;
          if (code === "DOMAIN_NOT_CONNECTED" || error.response?.status === 404) {
            setHostFailed(true);
            return;
          }
          setStoreError({
            code: detail?.error?.code || "STORE_NOT_FOUND",
            message: detail?.error?.message || "This BuildHof storefront does not exist.",
            subscriptionRequired: Boolean(detail?.error?.subscription_required),
          });
          setNotFound(true);
        });
      return;
    }

    // /preview waits for tenant bootstrap before a slug exists.
    if (!slug) {
      if (isPreviewRoute) return;
      setNotFound(true);
      return;
    }
    apiGet(`/storefront/${slug}${langQuery}`)
      .then((payload) => {
        setStoreData(payload);
        trackAnalyticsEvent({
          event_type: "page_view",
          slug: payload.business?.slug || slug,
          path: `/${payload.business?.slug || slug}`,
        });
      })
      .catch((error) => {
        const detail = error.response?.data?.detail;
        setStoreError({
          code: detail?.error?.code || "STORE_NOT_FOUND",
          message: detail?.error?.message || "This BuildHof storefront does not exist.",
          subscriptionRequired: Boolean(detail?.error?.subscription_required),
        });
        setNotFound(true);
      });
  }, [slug, language, customHost, params.slug, isPreviewRoute]);

  useEffect(() => {
    const business = storeData?.business;
    const storeName = business
      ? String(business.name || business.seo?.meta_title || "").trim()
      : loadingName;
    if (!storeName) return undefined;
    const previous = document.title;
    const suffix = business
      ? {
          menu: "Menu",
          checkout: "Checkout",
          legal: "Legal",
          status: "Order",
        }[view]
      : null;
    document.title = suffix ? `${storeName} · ${suffix}` : storeName;
    return () => {
      document.title = previous;
    };
  }, [storeData?.business, view, loadingName]);
  if (customHost && !params.slug && hostFailed) return <DomainNotConnected />;
  if (notFound) return <StoreUnavailable slug={slug} error={storeError} business={appData?.business} />;
  if (!storeData) {
    return <LoadingScreen message={loadingName ? `Loading ${loadingName}…` : "Loading…"} />;
  }
  if (view === "checkout") return <Checkout data={storeData} cart={cart} updateCartQuantity={updateCartQuantity} removeFromCart={removeFromCart} clearCart={clearCart} setOrder={setLastOrder} navigate={navigate} t={t} language={activeLanguage} setLanguage={setStoreLanguage} />;
  if (view === "status") return <StatusPage order={lastOrder} setOrder={setLastOrder} clearCart={clearCart} slug={slug} currency={storeData?.business?.currency || appData?.business?.currency} t={t} />;
  if (view === "legal") return <LegalPage data={storeData} docType={params.docType} t={t} language={activeLanguage} setLanguage={setStoreLanguage} />;
  if (view === "menu") return <FullMenuPage data={storeData} cart={cart} addToCart={addToCart} t={t} language={activeLanguage} setLanguage={setStoreLanguage} />;
  return <StoreHome data={storeData} cart={cart} addToCart={addToCart} t={t} language={activeLanguage} setLanguage={setStoreLanguage} />;
}

function StoreHome({ data, cart, addToCart, t, language, setLanguage }) {
  const business = data.business;
  const categories = data.categories;
  const products = data.products.filter((product) => product.visibility === "visible");
  const testimonials = data.testimonials || [];
  const campaign = data.campaign;
  const featuredProducts = campaign?.featured_product_ids?.length ? products.filter((product) => campaign.featured_product_ids.includes(product.id)).slice(0, 4) : [];
  const operations = business.store_operations || {};
  const menuLayout = business.menu_layout || "visual_cards";
  const previewMode = Boolean(data.preview_mode || business.preview_mode);
  const trackedAddToCart = (product) => addToCart(product, { slug: business.slug });

  useEffect(() => {
    if (!campaign?.id) return;
    trackAnalyticsEvent({
      event_type: "campaign_view",
      slug: business.slug,
      campaign_id: campaign.id,
    });
  }, [business.slug, campaign?.id]);
  const orderingPaused = previewMode || business.temporary_closure || operations.is_open === false;
  const hoursRows = groupOperatingHours(business.operating_hours);
  const theme = resolveStoreTheme(business);
  const currencyCode = business.currency;
  const deliveryApps = activeDeliveryPlatforms(business.delivery_platform_links);
  const sections = business.storefront_sections || [];
  const aboutSection = sections.find((section) => section.id === "about");
  const gallerySection = sections.find((section) => section.id === "gallery");
  const footerSection = sections.find((section) => section.id === "footer");
  const heroSection = sections.find((section) => section.id === "hero") || sections[0];
  const aboutVisible = aboutSection?.visible !== false;
  const galleryVisible = gallerySection?.visible !== false;
  const footerVisible = footerSection?.visible !== false;
  const aboutImage = resolveStoreMedia(aboutSection?.fields?.image || business.photos?.[1] || business.photos?.[0]);
  const galleryImages = (gallerySection?.fields?.images || []).filter(Boolean).map(resolveStoreMedia);
  const heroImage = resolveStoreMedia(heroSection?.fields?.background_image || business.photos?.[0]);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    if (!heroImage || typeof document === "undefined") return undefined;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = heroImage;
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [heroImage]);

  return (
    <main className="storefront-theme flex min-h-screen flex-col" data-testid="storefront-page" style={theme.cssVars}>
      {previewMode && <OwnerPreviewBanner business={business} />}
      <div className="flex-1">
      <header className="store-header sticky top-0 z-30 border-b backdrop-blur-xl" data-testid="storefront-topbar">
        {/* Mobile header */}
        <div className="pl-container flex min-h-[4.5rem] items-center gap-2 py-2.5 md:hidden">
          <StorefrontNavDrawer
            t={t}
            business={business}
            aboutVisible={aboutVisible}
            galleryVisible={galleryVisible && galleryImages.length > 0}
            galleryTitle={!gallerySection?.fields?.title || gallerySection.fields.title === "Gallery" ? t.gallery : gallerySection.fields.title}
            theme={theme}
          />
          <div className="min-w-0 flex-1">
            <StoreBrandLink
              business={business}
              logoClassName="h-11 w-auto max-h-11 max-w-[min(38vw,8.5rem)] object-contain"
            />
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <LanguageToggle language={language} setLanguage={setLanguage} business={business} />
            <Link data-testid="storefront-cart-link-mobile" to={storePath(business.slug, '/checkout')}>
              <Button className="store-btn-primary tap-target rounded-lg px-2.5" aria-label={`${t.cart} (${cartCount})`}>
                <ShoppingBag className="h-4 w-4" />
                <span>({cartCount})</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Desktop header — original layout */}
        <div className="pl-container hidden min-h-[5.5rem] items-center justify-between gap-3 py-3 md:flex">
          <StoreBrandLink business={business} />
          <nav className="flex items-center gap-2" data-testid="storefront-nav">
            <StorefrontNavLinks
              t={t}
              business={business}
              aboutVisible={aboutVisible}
              galleryVisible={galleryVisible && galleryImages.length > 0}
              galleryTitle={!gallerySection?.fields?.title || gallerySection.fields.title === "Gallery" ? t.gallery : gallerySection.fields.title}
              idPrefix="storefront-nav"
            />
          </nav>
          <LanguageToggle language={language} setLanguage={setLanguage} business={business} />
          <Link data-testid="storefront-cart-link" to={storePath(business.slug, '/checkout')}>
            <Button className="store-btn-primary tap-target rounded-lg">
              <ShoppingBag className="h-4 w-4" />
              {t.cart} ({cartCount})
            </Button>
          </Link>
        </div>
      </header>
      <section className="relative min-h-[78vh] overflow-hidden" data-testid="storefront-hero"><img data-testid="storefront-hero-image" src={heroImage} alt={`${business.name} dining room`} className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" decoding="async" /><div className="absolute inset-0 bg-gradient-to-r from-black/74 via-black/35 to-transparent" /><div className="pl-container relative z-10 flex min-h-[78vh] items-end pb-12 pt-24 text-white"><div className="max-w-3xl"><p className="rounded-full bg-white/16 px-3 py-1 text-sm font-bold backdrop-blur" data-testid="storefront-open-status">{orderingPaused ? t.closed : t.open} · {business.pickup_enabled ? t.pickup : ""}{business.pickup_enabled && business.delivery_enabled ? " + " : ""}{business.delivery_enabled ? t.delivery : ""}</p><h1 className="mt-5 text-4xl font-extrabold leading-[1.04] sm:text-5xl lg:text-6xl" data-testid="storefront-hero-title">{heroSection?.fields?.headline}</h1><p className="mt-5 max-w-xl text-lg leading-8 text-white/82" data-testid="storefront-hero-subtitle">{heroSection?.fields?.subtext}</p>{business.store_status_message && <p className="mt-4 rounded-lg bg-white/14 p-3 text-sm font-bold backdrop-blur" data-testid="storefront-status-message">{business.store_status_message}</p>}{business.temporary_closure && <p className="mt-3 rounded-lg bg-orange-500/86 p-3 text-sm font-bold" data-testid="storefront-closure-reason">{business.closure_reason || t.paused}</p>}<div className="mt-7 flex flex-wrap items-center gap-3" data-testid="storefront-hero-ctas"><a data-testid="storefront-order-now-anchor" href="#menu"><Button disabled={orderingPaused} className="store-btn-primary tap-target font-bold text-white hover:opacity-90">{orderingPaused ? t.paused : heroSection?.fields?.cta || t.start}</Button></a>{business.reservations_enabled ? <a data-testid="storefront-hero-reserve-button" href="#reserve" className="tap-target inline-flex h-11 items-center gap-2 rounded-lg border border-white/90 bg-black/55 px-5 text-sm font-bold text-white backdrop-blur-sm transition hover:bg-black/70"><CalendarCheck className="h-4 w-4" aria-hidden="true" />{t.reserveTitle}</a> : null}</div></div></div></section>
      {campaign && <section className="store-surface-muted py-8" data-testid="storefront-campaign-section"><div className="pl-container"><div className="grid gap-5 rounded-lg border bg-white p-5 shadow-sm md:grid-cols-[1fr_auto] md:items-center" data-testid="storefront-campaign-banner"><div><p className="store-text-primary text-xs font-bold uppercase" data-testid="storefront-campaign-status">{t.liveFeature}</p><h2 className="mt-2 text-3xl font-extrabold" data-testid="storefront-campaign-headline">{campaign.banner?.headline}</h2><p className="mt-2 text-muted-foreground" data-testid="storefront-campaign-body">{campaign.banner?.body}</p></div><a data-testid="storefront-campaign-cta-link" href="#menu" onClick={() => trackAnalyticsEvent({ event_type: "campaign_click", slug: business.slug, campaign_id: campaign.id })}><Button disabled={orderingPaused} className="store-btn-primary tap-target">{orderingPaused ? t.paused : campaign.banner?.cta_text || t.orderNow}</Button></a></div>{featuredProducts.length > 0 && <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="storefront-featured-products-grid">{featuredProducts.map((product) => <ProductCard key={product.id} product={product} addToCart={trackedAddToCart} compact orderingPaused={orderingPaused} currency={currencyCode} t={t} />)}</div>}</div></section>}
      <section className="pl-container py-12" id="menu" data-testid="storefront-menu-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-extrabold sm:text-4xl" data-testid="storefront-menu-title">{t.menu}</h2>
            <p className="mt-2 text-muted-foreground" data-testid="storefront-menu-body">{t.menuPreview}</p>
          </div>
          {products.length > HOME_MENU_PREVIEW_LIMIT && (
            <Link data-testid="storefront-full-menu-link" to={storePath(business.slug, '/menu')} className="store-link font-bold">
              {t.fullMenu}
            </Link>
          )}
        </div>
        <MenuBrowser
          categories={categories}
          products={products}
          business={business}
          menuLayout={menuLayout}
          addToCart={trackedAddToCart}
          orderingPaused={orderingPaused}
          currencyCode={currencyCode}
          t={t}
          preview
          previewLimit={HOME_MENU_PREVIEW_LIMIT}
        />
        {products.length > HOME_MENU_PREVIEW_LIMIT && (
          <div className="mt-8 flex justify-center" data-testid="storefront-full-menu-cta">
            <Link to={storePath(business.slug, '/menu')}>
              <Button variant="outline" className="tap-target rounded-lg font-bold store-text-primary" data-testid="storefront-full-menu-button" style={{ borderColor: "var(--store-primary)" }}>
                {t.fullMenu} · {products.length} items
              </Button>
            </Link>
          </div>
        )}
      </section>
      {aboutVisible && (
        <section className="store-surface-primary py-12 text-white" id="about" data-testid="storefront-about-section">
          <div className="pl-container grid gap-8 md:grid-cols-2">
            <div>
              <h2 className="text-3xl font-extrabold" data-testid="storefront-about-title">{t.story}</h2>
              <p className="mt-4 leading-8 text-white/78" data-testid="storefront-about-body">{aboutSection?.fields?.text}</p>
            </div>
            {aboutImage ? (
              <img data-testid="storefront-about-image" src={aboutImage} alt={`${business.name} story`} className="aspect-[4/3] w-full rounded-2xl object-cover" loading="lazy" decoding="async" />
            ) : null}
          </div>
        </section>
      )}
      {galleryVisible && galleryImages.length > 0 && (
        <GallerySection
          images={galleryImages}
          businessName={business.name}
          title={!gallerySection?.fields?.title || gallerySection.fields.title === "Gallery" ? t.gallery : gallerySection.fields.title}
          body={fmt(t.galleryBody, { name: business.name })}
        />
      )}
      {testimonials.length > 0 && (
        <TestimonialsCarousel testimonials={testimonials} t={t} />
      )}
      {business.reservations_enabled ? <ReservationSection business={business} previewMode={previewMode} t={t} /> : null}
      <section className="pl-container grid gap-4 py-12 md:grid-cols-3" id="hours" data-testid="storefront-info-section">
        <HoursInfo rows={hoursRows} title={t.hours} t={t} testId="storefront-hours-card" />
        <Info icon={<Truck />} title={t.delivery} body={fmt(t.deliveryMeta, { time: business.delivery_time, fee: formatMoney(business.delivery_fee || 0, currencyCode), radius: business.delivery_radius, unit: business.delivery_radius_unit === "km" ? t.km : t.mile })} testId="storefront-delivery-card" />
        <Info
          icon={<MapPin />}
          title={t.location}
          body={
            [
              business.address?.show_on_storefront ? formatBusinessAddress(business.address) : null,
              business.phone || null,
              business.email || null,
            ].filter(Boolean).join("\n") || "Address hidden"
          }
          testId="storefront-location-card"
        />
      </section>
      {deliveryApps.length > 0 && (
        <section className="pl-container pb-12" id="delivery-apps" data-testid="storefront-delivery-platforms-section">
          <h2 className="text-2xl font-extrabold" data-testid="storefront-delivery-platforms-title">Order on delivery apps</h2>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="storefront-delivery-platforms-body">Prefer a delivery partner? Order directly from the apps below.</p>
          <div className="mt-5 flex flex-wrap gap-3" data-testid="storefront-delivery-platforms-links">
            {deliveryApps.map((platform) => (
              <a
                key={platform.id}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`storefront-delivery-platform-${platform.id}-link`}
                className="tap-target inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-3 text-sm font-bold shadow-sm transition-colors hover:border-[var(--store-primary)] hover:text-[var(--store-primary)]"
              >
                {platform.name}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            ))}
          </div>
        </section>
      )}
      </div>
      <Link data-testid="storefront-floating-order-button" to={storePath(business.slug, '/checkout')} className="fixed bottom-4 left-4 right-4 z-40 md:hidden"><Button className="store-btn-accent tap-target w-full shadow-xl ring-2 ring-white/90">Order now · {cart.length} items</Button></Link>
      {footerVisible && <StoreFooter business={business} footerSection={footerSection} t={t} />}
    </main>
  );
}

function earliestReservationDateTime(noticeHours = 2) {
  const earliest = new Date(Date.now() + Math.max(0, Number(noticeHours) || 0) * 60 * 60 * 1000);
  const minutes = earliest.getMinutes();
  const rounded = Math.ceil(minutes / 15) * 15;
  if (rounded >= 60) {
    earliest.setHours(earliest.getHours() + 1);
    earliest.setMinutes(0, 0, 0);
  } else {
    earliest.setMinutes(rounded, 0, 0);
  }
  const pad = (value) => String(value).padStart(2, "0");
  return {
    date: `${earliest.getFullYear()}-${pad(earliest.getMonth() + 1)}-${pad(earliest.getDate())}`,
    time: `${pad(earliest.getHours())}:${pad(earliest.getMinutes())}`,
  };
}

function fmt(template, values = {}) {
  return String(template || "").replace(/\{(\w+)\}/g, (_, key) => (values[key] == null ? "" : String(values[key])));
}

function ReservationSection({ business, previewMode, t = i18n.en }) {
  const partyMin = Math.max(1, Number(business.reservation_party_min) || 2);
  const partyMax = Math.max(partyMin, Number(business.reservation_party_max) || 8);
  const noticeHours = Math.max(0, Number(business.reservation_min_notice_hours ?? 2));
  const earliest = earliestReservationDateTime(noticeHours);
  const [form, setForm] = useState({
    date: earliest.date,
    time: "19:30",
    party_size: String(Math.min(Math.max(4, partyMin), partyMax)),
    phone: "",
    name: "",
    email: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(null);

  const partyOptions = [];
  for (let size = partyMin; size <= partyMax; size += 1) partyOptions.push(size);

  const submit = async (event) => {
    event.preventDefault();
    if (previewMode) {
      toast.message(t.reservePreviewToast);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await apiPost(`/storefront/${business.slug}/reservations`, {
        customer: { name: form.name, email: form.email, phone: form.phone },
        party_size: Number(form.party_size),
        date: form.date,
        time: form.time,
      });
      setSubmitted(result.order);
      toast.success(t.reserveToastOk);
    } catch (err) {
      const message = err.response?.data?.detail?.error?.message || t.reserveToastFail;
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pl-container py-12" id="reserve" data-testid="storefront-reserve-section">
      <h2 className="text-3xl font-extrabold sm:text-4xl" data-testid="storefront-reserve-title">{t.reserveTitle}</h2>
      <p className="mt-2 text-muted-foreground" data-testid="storefront-reserve-body">
        {t.reserveBody}
      </p>
      <div className="mt-6 grid gap-8 rounded-2xl border store-surface-card p-6 shadow-sm md:grid-cols-[1.1fr_1fr] md:items-center md:p-8" data-testid="storefront-reserve-card">
        <div data-testid="storefront-reserve-copy">
          <h3 className="text-2xl font-extrabold" data-testid="storefront-reserve-copy-title">{t.reserveCopyTitle}</h3>
          <p className="mt-3 max-w-md text-sm leading-6 text-muted-foreground" data-testid="storefront-reserve-copy-body">
            {t.reserveCopyBody}
          </p>
          <div className="mt-5 flex flex-wrap gap-5 text-sm text-muted-foreground" data-testid="storefront-reserve-meta">
            <span className="inline-flex items-center gap-2" data-testid="storefront-reserve-party-meta">
              <Users className="h-4 w-4 store-text-primary" />
              {fmt(t.reserveGuests, { min: partyMin, max: partyMax })}
            </span>
            <span className="inline-flex items-center gap-2" data-testid="storefront-reserve-notice-meta">
              <Clock className="h-4 w-4 store-text-primary" />
              {noticeHours ? fmt(t.reserveNotice, { hours: noticeHours }) : t.reserveNoticeSameDay}
            </span>
          </div>
        </div>

        {submitted ? (
          <div className="rounded-xl bg-[color:var(--store-bg,#F8F5EE)] p-5" data-testid="storefront-reserve-success">
            <CheckCircle2 className="h-6 w-6 text-green-700" />
            <p className="mt-3 text-lg font-extrabold" data-testid="storefront-reserve-success-title">{t.reserveSuccessTitle}</p>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="storefront-reserve-success-body">
              {submitted.order_number} · {fmt(t.reserveGuestMany, { n: submitted.reservation?.party_size || form.party_size })} · {submitted.requested_time}
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              {fmt(t.reserveSuccessConfirm, { email: submitted.customer?.email || form.email })}
            </p>
          </div>
        ) : (
          <form noValidate onSubmit={submit} className="rounded-xl bg-[color:var(--store-bg,#F8F5EE)] p-5" data-testid="storefront-reserve-form">
            {error ? (
              <div className="mb-3 flex gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm font-bold text-orange-800" data-testid="storefront-reserve-error">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                {error}
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="reserve-date" data-testid="storefront-reserve-date-label">{t.reserveDate}</Label>
                <Input
                  id="reserve-date"
                  data-testid="storefront-reserve-date-input"
                  type="date"
                  min={earliest.date}
                  className="mt-1 h-11 rounded-lg bg-white"
                  value={form.date}
                  onChange={(event) => setForm({ ...form, date: event.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reserve-time" data-testid="storefront-reserve-time-label">{t.reserveTime}</Label>
                <Input
                  id="reserve-time"
                  data-testid="storefront-reserve-time-input"
                  type="time"
                  className="mt-1 h-11 rounded-lg bg-white"
                  value={form.time}
                  onChange={(event) => setForm({ ...form, time: event.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reserve-party" data-testid="storefront-reserve-party-label">{t.reserveParty}</Label>
                <select
                  id="reserve-party"
                  data-testid="storefront-reserve-party-select"
                  className="mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                  value={form.party_size}
                  onChange={(event) => setForm({ ...form, party_size: event.target.value })}
                >
                  {partyOptions.map((size) => (
                    <option key={size} value={size}>
                      {fmt(size === 1 ? t.reserveGuestOne : t.reserveGuestMany, { n: size })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="reserve-phone" data-testid="storefront-reserve-phone-label">{t.phone}</Label>
                <Input
                  id="reserve-phone"
                  data-testid="storefront-reserve-phone-input"
                  type="tel"
                  className="mt-1 h-11 rounded-lg bg-white"
                  placeholder={t.reservePhonePlaceholder}
                  value={form.phone}
                  onChange={(event) => setForm({ ...form, phone: event.target.value })}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="reserve-name" data-testid="storefront-reserve-name-label">{t.name}</Label>
                <Input
                  id="reserve-name"
                  data-testid="storefront-reserve-name-input"
                  className="mt-1 h-11 rounded-lg bg-white"
                  placeholder={t.reserveNamePlaceholder}
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="reserve-email" data-testid="storefront-reserve-email-label">{t.email}</Label>
                <Input
                  id="reserve-email"
                  data-testid="storefront-reserve-email-input"
                  type="email"
                  className="mt-1 h-11 rounded-lg bg-white"
                  placeholder={t.reserveEmailPlaceholder}
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy || previewMode}
              className="store-btn-primary tap-target mt-4 w-full rounded-lg font-bold"
              data-testid="storefront-reserve-submit"
            >
              {busy ? t.reserveSending : previewMode ? t.reservePreview : t.reserveSubmit}
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="storefront-reserve-note">
              {t.reserveNote}
            </p>
            <p className="mt-1 text-center text-xs text-muted-foreground" data-testid="storefront-reserve-hold-note">
              {t.reserveHoldNote}
            </p>
          </form>
        )}
      </div>
    </section>
  );
}

const TESTIMONIAL_QUOTE_LIMIT = 160;

function TestimonialCard({ item, t = i18n.en }) {
  const quote = String(item.quote || "").trim();
  const needsTruncate = quote.length > TESTIMONIAL_QUOTE_LIMIT;
  const [expanded, setExpanded] = useState(false);
  const rating = Math.max(0, Math.min(5, Number(item.rating) || 0));
  const displayQuote = needsTruncate && !expanded
    ? `${quote.slice(0, TESTIMONIAL_QUOTE_LIMIT).trimEnd()}…`
    : quote;

  return (
    <article
      className="flex h-full flex-col rounded-lg border border-white/15 store-surface-card p-5 text-[color:var(--store-text,#171717)] shadow-sm"
      data-testid={`storefront-testimonial-${item.id}`}
    >
      <p
        className="text-sm font-bold tracking-wide"
        style={{ color: "#FBBC04" }}
        data-testid={`storefront-testimonial-${item.id}-rating`}
        aria-label={`${rating} out of 5 stars`}
      >
        {"★".repeat(rating)}
        <span className="text-[#E8EAED]">{"★".repeat(5 - rating)}</span>
      </p>
      <p
        className="mt-3 flex-1 text-lg font-extrabold leading-7"
        data-testid={`storefront-testimonial-${item.id}-quote`}
      >
        “{displayQuote}”
      </p>
      {needsTruncate ? (
        <button
          type="button"
          className="mt-2 self-start text-sm font-bold store-text-primary underline-offset-2 hover:underline"
          data-testid={`storefront-testimonial-${item.id}-toggle`}
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
        >
          {expanded ? t.showLess : t.showMore}
        </button>
      ) : null}
      <p
        className="mt-4 text-sm font-bold text-muted-foreground"
        data-testid={`storefront-testimonial-${item.id}-name`}
      >
        {item.customer_name}
      </p>
    </article>
  );
}

function GallerySection({ images = [], businessName, title, body }) {
  const showControls = images.length > 1;

  return (
    <section className="pl-container py-12" id="gallery" data-testid="storefront-gallery-section">
      <h2 className="text-3xl font-extrabold" data-testid="storefront-gallery-title">{title}</h2>
      <p className="mt-2 text-muted-foreground" data-testid="storefront-gallery-body">{body}</p>

      {/* Mobile carousel */}
      <Carousel
        opts={{ align: "start", loop: images.length > 1 }}
        className="mt-6 w-full md:hidden"
        data-testid="storefront-gallery-carousel"
      >
        <CarouselContent className="-ml-3">
          {images.map((url, index) => (
            <CarouselItem
              key={`${url}-${index}`}
              className="basis-[88%] pl-3"
              data-testid={`storefront-gallery-slide-${index}`}
            >
              <img
                src={url}
                alt={`${businessName} gallery ${index + 1}`}
                className="aspect-[4/3] w-full rounded-2xl object-cover"
                loading="lazy"
                decoding="async"
                data-testid={`storefront-gallery-carousel-image-${index}`}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        {showControls ? (
          <div className="mt-4 flex items-center justify-end gap-2" data-testid="storefront-gallery-controls">
            <CarouselPrevious className="static left-auto top-auto h-10 w-10 translate-x-0 translate-y-0 rounded-full" />
            <CarouselNext className="static left-auto top-auto h-10 w-10 translate-x-0 translate-y-0 rounded-full" />
          </div>
        ) : null}
      </Carousel>

      {/* Desktop / tablet grid */}
      <div className="mt-6 hidden gap-3 md:grid md:grid-cols-2 lg:grid-cols-3" data-testid="storefront-gallery-grid">
        {images.map((url, index) => (
          <img
            key={`${url}-${index}-grid`}
            src={url}
            alt={`${businessName} gallery ${index + 1}`}
            className="aspect-[4/3] w-full rounded-2xl object-cover"
            loading="lazy"
            decoding="async"
            data-testid={`storefront-gallery-image-${index}`}
          />
        ))}
      </div>
    </section>
  );
}

function TestimonialsCarousel({ testimonials = [], t = i18n.en }) {
  const showControls = testimonials.length > 1;

  return (
    <section className="store-surface-primary py-12 text-white" data-testid="storefront-testimonials-section">
      <div className="pl-container">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-extrabold uppercase text-white/78" data-testid="storefront-testimonials-kicker">
              {t.reviewsKicker}
            </p>
            <h2 className="mt-2 text-3xl font-extrabold" data-testid="storefront-testimonials-title">
              {t.reviewsTitle}
            </h2>
          </div>
        </div>
        <Carousel
          opts={{ align: "start", loop: testimonials.length > 3 }}
          className="mt-6 w-full"
          data-testid="storefront-testimonials-carousel"
        >
          <CarouselContent className="-ml-4" data-testid="storefront-testimonials-grid">
            {testimonials.map((item) => (
              <CarouselItem
                key={item.id}
                className="basis-[88%] pl-4 sm:basis-1/2 lg:basis-1/3"
                data-testid={`storefront-testimonial-slide-${item.id}`}
              >
                <TestimonialCard item={item} t={t} />
              </CarouselItem>
            ))}
          </CarouselContent>
          {showControls ? (
            <div className="mt-5 flex items-center justify-end gap-2" data-testid="storefront-testimonials-controls">
              <CarouselPrevious
                className="static left-auto top-auto h-10 w-10 translate-x-0 translate-y-0 rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white disabled:opacity-40"
              />
              <CarouselNext
                className="static left-auto top-auto h-10 w-10 translate-x-0 translate-y-0 rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white disabled:opacity-40"
              />
            </div>
          ) : null}
        </Carousel>
      </div>
    </section>
  );
}

function StoreFooter({ business, footerSection, t = i18n.en }) {
  const fields = footerSection?.fields || {};
  const email = String(fields.contact_email || business.email || "").trim();
  const phone = String(fields.contact_phone || business.phone || "").trim();
  const address = String(fields.contact_address || formatBusinessAddress(business.address) || "").trim();
  const tagline = String(fields.tagline || business.description || "").trim();
  const legalDocs = activeLegalDocuments(business.legal_documents);
  const social = business.social_links || {};
  return (
    <footer className="store-surface-primary mt-auto shrink-0 border-t text-white" data-testid="storefront-footer">
      <div className="pl-container grid items-start gap-8 py-10 sm:gap-10 md:grid-cols-3 md:py-12">
        <div className="min-w-0" data-testid="storefront-footer-brand">
          {resolveStoreMedia(business.logo_url) ? (
            <div className="inline-flex rounded-lg bg-white/95 p-2" data-testid="storefront-footer-logo-wrap">
              <img
                src={resolveStoreMedia(business.logo_url)}
                alt={business.name}
                className="h-10 w-auto max-w-[160px] object-contain"
                data-testid="storefront-footer-logo"
              />
            </div>
          ) : (
            <p className="text-2xl font-extrabold leading-tight" data-testid="storefront-footer-name">{business.name}</p>
          )}
          {tagline ? <p className="mt-3 text-sm leading-6 text-white/72" data-testid="storefront-footer-tagline">{tagline}</p> : null}
          {(social.instagram || social.facebook) ? (
            <div className="mt-4 flex flex-wrap gap-3 text-sm font-bold" data-testid="storefront-footer-social">
              {social.instagram ? <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="text-white/85 hover:text-white" data-testid="storefront-footer-instagram">Instagram</a> : null}
              {social.facebook ? <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="text-white/85 hover:text-white" data-testid="storefront-footer-facebook">Facebook</a> : null}
            </div>
          ) : null}
        </div>
        <div className="min-w-0" data-testid="storefront-footer-contact">
          <p className="text-xs font-bold uppercase tracking-wide text-white/55">{t.footerContact}</p>
          <div className="mt-3 space-y-2 text-sm leading-6 text-white/85">
            {phone ? <p data-testid="storefront-footer-phone"><a href={`tel:${phone.replace(/\s+/g, "")}`} className="text-white/85 hover:text-white">{phone}</a></p> : null}
            {email ? <p data-testid="storefront-footer-email"><a href={`mailto:${email}`} className="break-all text-white/85 hover:text-white">{email}</a></p> : null}
            {address ? <p data-testid="storefront-footer-address">{address}</p> : null}
            {!phone && !email && !address ? <p className="text-white/55" data-testid="storefront-footer-contact-empty">Contact details coming soon.</p> : null}
          </div>
        </div>
        <div className="min-w-0" data-testid="storefront-footer-legal">
          <p className="text-xs font-bold uppercase tracking-wide text-white/55">{t.footerLegal}</p>
          <nav className="mt-3 flex flex-col gap-2 text-sm leading-6" data-testid="storefront-footer-legal-links">
            {legalDocs.length > 0 ? legalDocs.map((doc) => (
              <Link key={doc.id} to={storePath(business.slug, `/legal/${doc.id}`)} className="font-normal text-white/85 hover:text-white" data-testid={`storefront-footer-legal-${doc.id}`}>
                {doc.label}
              </Link>
            )) : (
              <p className="font-normal text-white/55" data-testid="storefront-footer-legal-empty">Legal pages will appear here once uploaded.</p>
            )}
          </nav>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="pl-container flex flex-wrap items-center justify-between gap-2 py-4 text-xs text-white/55">
          <span>© {new Date().getFullYear()} {business.name}</span>
          <span>
            Powered by{" "}
            <a
              href="https://buildhof.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-inherit underline-offset-2 hover:underline"
              data-testid="storefront-footer-powered-by-link"
            >
              BuildHof
            </a>
            . A Build Up Rise Up UG company.
          </span>
        </div>
        {/* Space for the fixed mobile order button */}
        <div className="h-24 md:hidden" aria-hidden="true" />
      </div>
    </footer>
  );
}

function LegalPage({ data, docType, t, language, setLanguage }) {
  const business = data.business;
  const theme = resolveStoreTheme(business);
  const legalDocs = activeLegalDocuments(business.legal_documents);
  const doc = legalDocs.find((item) => item.id === docType);
  const updatedFromText = doc?.content_text ? extractLegalUpdatedLabel(doc.content_text) : null;
  const updatedLabel = updatedFromText || formatLegalUpdatedDate(doc?.uploaded_at);
  const bodyHtml = doc?.content_text
    ? formatLegalHtml(doc.content_text, { title: doc.label })
    : "";
  const otherDocs = legalDocs.filter((item) => item.id !== doc?.id);

  return (
    <main className="storefront-theme flex min-h-screen flex-col bg-white" data-testid="storefront-legal-page" style={theme.cssVars}>
      <header className="store-header sticky top-0 z-30 border-b bg-white/90 backdrop-blur-xl" data-testid="storefront-legal-topbar">
        <div className="pl-container flex min-h-[5.5rem] items-center justify-between gap-3 py-3">
          <Link data-testid="storefront-legal-brand-link" to={storePath(business.slug)} className="flex min-w-0 items-center gap-2.5 text-xl font-extrabold tracking-tight" aria-label={business.name}>
            {resolveStoreMedia(business.logo_url) ? (
              <img src={resolveStoreMedia(business.logo_url)} alt={business.name} className="h-16 w-auto max-h-16 max-w-[240px] object-contain" data-testid="storefront-legal-logo" />
            ) : (
              business.name
            )}
          </Link>
          <LanguageToggle language={language} setLanguage={setLanguage} business={business} />
        </div>
      </header>

      <section className="flex-1 bg-slate-50 pb-20 pt-12 md:pb-24 md:pt-20" data-testid="storefront-legal-document">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Link data-testid="storefront-legal-back-link" to={storePath(business.slug)} className="store-link text-sm font-bold">
            {t.back}
          </Link>

          {doc ? (
            <article className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10" data-testid={`storefront-legal-doc-${doc.id}`}>
              <p className="store-text-primary mb-4 block text-xs font-bold uppercase tracking-[0.2em]" data-testid="storefront-legal-eyebrow">
                {t.footerLegal || "Legal"}
              </p>
              <div className="mb-8 flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-6">
                <h1 className="max-w-xl text-4xl font-bold tracking-tight text-[color:var(--store-text,#0f172a)] md:text-5xl" data-testid="storefront-legal-title">
                  {doc.label}
                </h1>
                {updatedLabel ? (
                  <div className="legal-meta !mb-0" data-testid="storefront-legal-meta">
                    <div className="legal-meta-row">
                      <dt>Last updated</dt>
                      <dd data-testid="storefront-legal-updated">{updatedLabel}</dd>
                    </div>
                  </div>
                ) : null}
              </div>

              {bodyHtml ? (
                <div
                  className="prose-legal"
                  data-testid="storefront-legal-content"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              ) : (
                <p className="mt-6 text-base leading-7 text-slate-500" data-testid="storefront-legal-missing-content">
                  This document was uploaded, but its text could not be extracted yet. Upload a text-based PDF or DOCX to display it inline.
                </p>
              )}

              {otherDocs.length > 0 ? (
                <nav className="mt-16 border-t border-slate-200 pt-8" data-testid="storefront-legal-related">
                  <p className="text-sm font-bold text-slate-900">Other legal pages</p>
                  <ul className="mt-4 flex flex-col gap-2">
                    {otherDocs.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={storePath(business.slug, `/legal/${item.id}`)}
                          className="store-link text-sm font-semibold"
                          data-testid={`storefront-legal-related-${item.id}`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              ) : null}
            </article>
          ) : (
            <article className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10" data-testid="storefront-legal-missing">
              <p className="store-text-primary mb-4 block text-xs font-bold uppercase tracking-[0.2em]">Legal</p>
              <h1 className="mb-4 text-4xl font-bold tracking-tight" data-testid="storefront-legal-missing-title">
                {docType ? legalDocumentLabel(docType) : "Legal pages"}
              </h1>
              <p className="text-base leading-7 text-slate-500" data-testid="storefront-legal-missing-body">
                This document is not available yet. Choose another legal page below.
              </p>
              <nav className="mt-8 flex flex-col gap-2" data-testid="storefront-legal-available-list">
                {legalDocs.map((item) => (
                  <Link key={item.id} to={storePath(business.slug, `/legal/${item.id}`)} className="store-link font-bold" data-testid={`storefront-legal-available-${item.id}`}>
                    {item.label}
                  </Link>
                ))}
                {!legalDocs.length && <p className="text-sm text-slate-500">No legal documents have been published.</p>}
              </nav>
            </article>
          )}
        </div>
      </section>

      <StoreFooter business={business} footerSection={(business.storefront_sections || []).find((section) => section.id === "footer")} t={t} />
    </main>
  );
}


function storefrontNavItems({ t, business, aboutVisible, galleryVisible, galleryTitle }) {
  const items = [{ id: "menu", href: "#menu", label: t.menu }];
  if (business.reservations_enabled) items.push({ id: "reserve", href: "#reserve", label: t.reserve });
  if (aboutVisible) items.push({ id: "about", href: "#about", label: t.about });
  if (galleryVisible) items.push({ id: "gallery", href: "#gallery", label: galleryTitle || t.gallery });
  items.push({ id: "hours", href: "#hours", label: t.hours });
  return items;
}

function StorefrontNavLinks({
  t,
  business,
  aboutVisible,
  galleryVisible,
  galleryTitle,
  idPrefix = "storefront-nav",
  linkClassName = "tap-target rounded-lg px-3 py-2 font-bold",
}) {
  return storefrontNavItems({ t, business, aboutVisible, galleryVisible, galleryTitle }).map((item) => (
    <a key={item.id} data-testid={`${idPrefix}-${item.id}-link`} href={item.href} className={linkClassName}>
      {item.label}
    </a>
  ));
}

function StorefrontNavDrawer({ t, business, aboutVisible, galleryVisible, galleryTitle, theme }) {
  const [open, setOpen] = useState(false);
  const items = storefrontNavItems({ t, business, aboutVisible, galleryVisible, galleryTitle });

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="tap-target h-9 w-9 shrink-0"
          data-testid="storefront-nav-drawer-trigger"
          aria-label={t.menu}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="storefront-theme flex w-[min(100%,20rem)] flex-col gap-0 p-0"
        style={theme?.cssVars}
        data-testid="storefront-nav-drawer"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader className="border-b px-5 py-5 text-left">
          <SheetTitle className="text-xl font-extrabold" data-testid="storefront-nav-drawer-title">
            {business.name}
          </SheetTitle>
        </SheetHeader>
        <nav className="flex flex-col p-3" aria-label={t.menu} data-testid="storefront-nav-drawer-links">
          {items.map((item) => (
            <a
              key={item.id}
              href={item.href}
              data-testid={`storefront-nav-drawer-${item.id}-link`}
              className="tap-target flex w-full items-center rounded-lg px-3 py-3 text-left text-base font-bold transition-colors hover:bg-black/[0.04]"
              onClick={(event) => {
                event.preventDefault();
                const id = item.id;
                setOpen(false);
                // Wait for sheet close + any residual body scroll lock to clear.
                window.setTimeout(() => {
                  const target = document.getElementById(id);
                  if (!target) return;
                  const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - 88);
                  window.scrollTo({ top, behavior: "smooth" });
                  try {
                    window.history.replaceState(null, "", `#${id}`);
                  } catch {
                    /* ignore */
                  }
                }, 300);
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

function LanguageToggle({ language, setLanguage, business }) {
  const options = storefrontLanguageOptions(business);
  if (options.length < 2) return null;
  const activeLabel = options.find(([code]) => code === language)?.[1] || String(language || "").toUpperCase();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="tap-target h-9 gap-1 rounded-lg px-2.5 text-xs font-extrabold md:hidden"
            data-testid="storefront-language-dropdown-trigger"
            aria-label={activeLabel}
          >
            {activeLabel}
            <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[7.5rem]" data-testid="storefront-language-dropdown">
          {options.map(([code, label]) => (
            <DropdownMenuItem
              key={code}
              data-testid={`storefront-language-${code}-option`}
              className="cursor-pointer justify-between gap-3 font-bold"
              onSelect={() => setLanguage(code)}
            >
              {label}
              {language === code ? <Check className="h-4 w-4" aria-hidden="true" /> : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="hidden shrink-0 gap-1 rounded-full border bg-white p-1 md:flex" data-testid="storefront-language-toggle">
        {options.map(([code, label]) => (
          <button
            key={code}
            type="button"
            data-testid={`storefront-language-${code}-button`}
            className={`tap-target rounded-full px-2 py-1 text-xs font-extrabold ${language === code ? "store-chip-active" : "text-muted-foreground"}`}
            onClick={() => setLanguage(code)}
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );
}

/** Dedicated full-menu page opened by the "Full menu" link. */
function FullMenuPage({ data, cart, addToCart, t, language, setLanguage }) {
  const business = data.business;
  const categories = data.categories || [];
  const products = (data.products || []).filter((product) => product.visibility === "visible");
  const operations = business.store_operations || {};
  const menuLayout = business.menu_layout || "visual_cards";
  const previewMode = Boolean(data.preview_mode || business.preview_mode);
  const orderingPaused = previewMode || business.temporary_closure || operations.is_open === false;
  const theme = resolveStoreTheme(business);
  const currencyCode = business.currency;
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const trackedAddToCart = (product) => addToCart(product, { slug: business.slug });

  return (
    <main className="storefront-theme min-h-screen safe-bottom" data-testid="storefront-full-menu-page" style={theme.cssVars}>
      {previewMode && <OwnerPreviewBanner business={business} />}
      <header className="store-header sticky top-0 z-30 border-b backdrop-blur-xl" data-testid="storefront-full-menu-topbar">
        <div className="pl-container flex min-h-[4.5rem] items-center justify-between gap-2 py-2.5 md:min-h-[5.5rem] md:gap-3 md:py-3">
          <div className="min-w-0">
            <StoreBrandLink
              business={business}
              testId="storefront-full-menu-brand-link"
              logoTestId="storefront-full-menu-logo"
              nameTestId="storefront-full-menu-business-name"
              logoClassName="h-11 w-auto max-h-11 max-w-[min(38vw,8.5rem)] object-contain md:h-16 md:max-h-16 md:max-w-[240px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle language={language} setLanguage={setLanguage} business={business} />
            <Link data-testid="storefront-full-menu-cart-link" to={storePath(business.slug, '/checkout')}>
              <Button className="store-btn-primary tap-target rounded-lg px-2.5 md:px-4" aria-label={`${t.cart} (${cartCount})`}>
                <ShoppingBag className="h-4 w-4" />
                <span className="hidden md:inline">{t.cart}</span>
                <span>({cartCount})</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="pl-container py-8 md:py-12" data-testid="storefront-full-menu-section">
        <Link data-testid="storefront-full-menu-back-link" to={storePath(business.slug)} className="store-link text-sm font-bold">
          {t.back}
        </Link>
        <h1 className="mt-4 text-3xl font-extrabold sm:text-4xl" data-testid="storefront-full-menu-title">{t.fullMenu}</h1>
        <p className="mt-2 text-muted-foreground" data-testid="storefront-full-menu-body">
          {menuLayout === "compact_list" ? t.menuCompact : t.menuVisual}
        </p>
        <MenuBrowser
          categories={categories}
          products={products}
          business={business}
          menuLayout={menuLayout}
          addToCart={trackedAddToCart}
          orderingPaused={orderingPaused}
          currencyCode={currencyCode}
          t={t}
        />
      </section>

      <Link data-testid="storefront-full-menu-floating-order-button" to={storePath(business.slug, '/checkout')} className="fixed bottom-4 left-4 right-4 z-40 md:hidden">
        <Button className="store-btn-accent tap-target w-full shadow-xl ring-2 ring-white/90">Order now · {cart.length} items</Button>
      </Link>
    </main>
  );
}

function MenuBrowser({ categories, products, business, menuLayout, addToCart, orderingPaused, currencyCode, t, preview = false, previewLimit }) {
  const [activeFilters, setActiveFilters] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState("all");
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const categoryById = useMemo(
    () => Object.fromEntries((categories || []).map((category) => [category.id, category])),
    [categories]
  );
  const filterOptions = buildFilterOptions(products);
  const categoryFilteredProducts = activeCategoryId === "all"
    ? products
    : products.filter((product) => product.category_id === activeCategoryId);
  const filteredProducts = filterProducts(categoryFilteredProducts, activeFilters);
  const isDefaultBrowse = activeCategoryId === "all" && activeFilters.length === 0;
  const displayProducts = preview && previewLimit && isDefaultBrowse
    ? filteredProducts.slice(0, previewLimit)
    : filteredProducts;
  const toggleFilter = (filter) => setActiveFilters((current) => (
    current.includes(filter) ? current.filter((item) => item !== filter) : [...current, filter]
  ));
  const activeCategory = activeCategoryId === "all" ? null : categoryById[activeCategoryId];
  const showLunchWeekendBanner = Boolean(
    activeCategory
    && isLunchMenuCategory(activeCategory)
    && !isWeekday(now)
  );
  const bufferMinutes = kitchenCloseMinutesForBusiness(business);

  return (
    <div data-testid={preview ? "storefront-menu-preview" : "storefront-menu-browser"}>
      <CategoryPills
        categories={categories}
        activeCategoryId={activeCategoryId}
        onSelect={setActiveCategoryId}
        t={t}
      />
      {showLunchWeekendBanner ? (
        <div
          className="mt-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-bold text-orange-900"
          data-testid="storefront-lunch-weekend-banner"
        >
          {t.lunchWeekendBanner}
        </div>
      ) : null}
      <MenuFilters
        filters={filterOptions}
        activeFilters={activeFilters}
        toggleFilter={toggleFilter}
        clearFilters={() => setActiveFilters([])}
      />
      {menuLayout === "compact_list" ? (
        <CompactMenuList
          categories={categories}
          products={displayProducts}
          business={business}
          now={now}
          bufferMinutes={bufferMinutes}
          addToCart={addToCart}
          orderingPaused={orderingPaused}
          currency={currencyCode}
          t={t}
        />
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="storefront-product-grid">
          {displayProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              category={categoryById[product.category_id]}
              business={business}
              now={now}
              bufferMinutes={bufferMinutes}
              addToCart={addToCart}
              orderingPaused={orderingPaused}
              currency={currencyCode}
              t={t}
            />
          ))}
          {!displayProducts.length && (
            <div className="rounded-lg border store-surface-card p-6 md:col-span-2 xl:col-span-3" data-testid="storefront-filter-empty-state">
              <h3 className="font-extrabold">No items match these filters.</h3>
              <p className="mt-1 text-sm text-muted-foreground">Clear filters or ask the business about alternatives.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryPills({ categories = [], activeCategoryId = "all", onSelect, t = i18n.en }) {
  if (!categories.length) return null;
  return (
    <div className="mt-6" data-testid="storefront-category-pills-wrap">
      <div className="flex flex-wrap gap-2" data-testid="storefront-category-pills">
        <button
          type="button"
          data-testid="storefront-category-all-button"
          onClick={() => onSelect?.("all")}
          className={`inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-full border px-4 text-sm font-bold transition-colors ${
            activeCategoryId === "all" ? "store-chip-active" : "store-chip-idle"
          }`}
        >
          {t.all}
        </button>
        {categories.map((category) => {
          const active = activeCategoryId === category.id;
          const lunch = isLunchMenuCategory(category);
          return (
            <button
              key={category.id}
              type="button"
              data-testid={`storefront-category-${category.id}-button`}
              title={lunch ? `${category.name} · ${t.lunchBadgeLong}` : category.name}
              onClick={() => onSelect?.(category.id)}
              className={`inline-flex min-h-11 max-w-full shrink-0 items-center gap-2 rounded-full border px-4 text-left text-sm font-bold transition-colors ${
                active ? "store-chip-active" : "store-chip-idle"
              }`}
            >
              <span className="break-words">{category.name}</span>
              {lunch ? (
                <span
                  className="shrink-0 rounded-full bg-black/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide sm:text-xs"
                  data-testid={`storefront-category-${category.id}-lunch-badge`}
                >
                  <span className="sm:hidden">{t.lunchBadgeShort}</span>
                  <span className="hidden sm:inline">{t.lunchBadgeLong}</span>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function buildFilterOptions(products) { const dietary = [...new Set(products.flatMap((product) => product.dietary_markers || []))].sort().map((value) => ({ key: `dietary:${value}`, label: value, type: "dietary" })); const allergens = [...new Set(products.flatMap((product) => product.allergens || []))].sort().map((value) => ({ key: `exclude:${value}`, label: `Hide ${value}`, type: "allergen" })); return [...dietary, ...allergens]; }
function filterProducts(products, activeFilters) { if (!activeFilters.length) return products; return products.filter((product) => activeFilters.every((filter) => { const [mode, value] = filter.split(":"); if (mode === "dietary") return (product.dietary_markers || []).includes(value); if (mode === "exclude") return !(product.allergens || []).includes(value); return true; })); }
function MenuFilters({ filters, activeFilters, toggleFilter, clearFilters }) {
  if (!filters.length) return null;
  return (
    <div className="mt-4 rounded-lg border store-surface-card p-4" data-testid="storefront-menu-filters">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-extrabold" data-testid="storefront-menu-filters-title">Dietary &amp; allergen filters</p>
        {activeFilters.length > 0 && (
          <button data-testid="storefront-clear-filters-button" className="store-link text-sm font-bold" onClick={clearFilters}>Clear filters</button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="storefront-menu-filter-chips">
        {filters.map((filter) => (
          <button
            key={filter.key}
            type="button"
            data-testid={`storefront-filter-${filter.key.replace(":", "-")}-button`}
            title={filter.label}
            className={`tap-target max-w-full shrink-0 rounded-full border px-3 py-2 text-left text-sm font-bold transition-colors ${
              activeFilters.includes(filter.key) ? "store-chip-active" : "store-chip-idle store-surface-muted"
            }`}
            onClick={() => toggleFilter(filter.key)}
          >
            <span className="block max-w-[14rem] truncate sm:max-w-[18rem]">{filter.label}</span>
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground" data-testid="storefront-allergen-disclaimer">
        Allergen information is provided by the business. Please confirm with the team before ordering if you have a severe allergy.
      </p>
    </div>
  );
}
function ProductBadges({ product, prefix }) { const allergens = product.allergens || []; const markers = product.dietary_markers || []; if (!allergens.length && !markers.length) return null; return <div className="mt-2 flex flex-wrap gap-1" data-testid={`${prefix}-badges`}>{markers.map((marker) => <span key={marker} data-testid={`${prefix}-dietary-${marker}`} className="store-badge-soft rounded-full px-2 py-0.5 text-xs font-bold">{marker}</span>)}{allergens.map((allergen) => <span key={allergen} data-testid={`${prefix}-allergen-${allergen}`} className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-bold text-orange-800">{allergen}</span>)}</div>; }
const IMPORT_PLACEHOLDER_DESCRIPTIONS = new Set([
  "imported from menu file.",
  "imported from menu file",
  "imported from onboarding.",
  "imported from onboarding",
  "imported from onboarding menu text.",
  "imported from onboarding menu text",
]);
function productPublicDescription(product) {
  const description = String(product?.description || "").trim();
  if (!description) return "";
  if (IMPORT_PLACEHOLDER_DESCRIPTIONS.has(description.toLowerCase())) return "";
  return description;
}
function ProductCard({ product, category, business, now, bufferMinutes, addToCart, compact = false, orderingPaused = false, currency, t = i18n.en }) {
  const description = productPublicDescription(product);
  const [imageSrc, setImageSrc] = useState(() => resolveProductImageUrl(product.images));
  const availability = isProductOrderableNow(product, category, business, now || new Date(), bufferMinutes);
  const blocked = !availability.orderable;
  const showKitchenNotice = blocked && availability.reason === "kitchen_closed";
  const buttonLabel = orderingPaused
    ? t.paused
    : product.sold_out
      ? t.soldOut
      : blocked
        ? t.notAvailable
        : t.addToCart;

  useEffect(() => {
    setImageSrc(resolveProductImageUrl(product.images));
  }, [product.id, product.images]);

  return (
    <article className="overflow-hidden rounded-lg border store-surface-card shadow-sm" data-testid={`storefront-product-${product.id}`}>
      <div className="relative">
        <img
          data-testid={`storefront-product-${product.id}-image`}
          src={imageSrc}
          alt={product.name}
          className="aspect-[4/3] w-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => {
            const fallback = defaultProductImageUrl(800);
            setImageSrc((current) => (current === fallback ? current : fallback));
          }}
        />
        {product.campaign_badge?.label && (
          <span data-testid={`storefront-product-${product.id}-campaign-badge`} className="store-badge-primary absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold shadow">
            {product.campaign_badge.label}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-extrabold" data-testid={`storefront-product-${product.id}-name`}>{product.name}</h3>
          <Money amount={product.price} currency={currency} testId={`storefront-product-${product.id}-price`} />
        </div>
        {description ? (
          <p className={`mt-2 text-sm text-muted-foreground ${compact ? "line-clamp-2" : ""}`} data-testid={`storefront-product-${product.id}-description`}>
            {description}
          </p>
        ) : null}
        <ProductBadges product={product} prefix={`storefront-product-${product.id}`} />
        {showKitchenNotice ? (
          <p className="mt-3 text-xs font-bold text-muted-foreground" data-testid={`storefront-product-${product.id}-kitchen-notice`}>
            {t.kitchenCloseNotice}
          </p>
        ) : null}
        <Button
          data-testid={`storefront-product-${product.id}-add-button`}
          disabled={product.sold_out || orderingPaused || blocked}
          className="store-btn-accent tap-target mt-4 w-full disabled:opacity-55"
          onClick={() => addToCart(product)}
        >
          {buttonLabel}
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </article>
  );
}
function CompactMenuList({ categories, products, business, now, bufferMinutes, addToCart, orderingPaused, currency, t = i18n.en }) {
  const grouped = categories.map((category) => ({ ...category, products: products.filter((product) => product.category_id === category.id) })).filter((category) => category.products.length);
  const uncategorized = products.filter((product) => !categories.find((category) => category.id === product.category_id));
  const sections = [...grouped, ...(uncategorized.length ? [{ id: "uncategorized", name: "More", products: uncategorized }] : [])];
  return (
    <div className="mt-6 overflow-hidden rounded-lg border store-surface-card shadow-sm" data-testid="storefront-compact-menu-list">
      {sections.length ? sections.map((category) => (
        <section key={category.id} className="border-b last:border-b-0" data-testid={`compact-menu-category-${category.id}`}>
          <div className="store-surface-muted px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-extrabold" data-testid={`compact-menu-category-${category.id}-title`}>{category.name}</h3>
              {isLunchMenuCategory(category) ? (
                <span className="rounded-full bg-black/10 px-2 py-0.5 text-xs font-extrabold" data-testid={`compact-menu-category-${category.id}-lunch-badge`}>
                  {t.lunchBadgeLong}
                </span>
              ) : null}
            </div>
          </div>
          <div>
            {category.products.map((product) => {
              const description = productPublicDescription(product);
              const availability = isProductOrderableNow(product, category.id === "uncategorized" ? null : category, business, now || new Date(), bufferMinutes);
              const blocked = !availability.orderable;
              const showKitchenNotice = blocked && availability.reason === "kitchen_closed";
              const buttonLabel = orderingPaused
                ? t.paused
                : product.sold_out
                  ? t.soldOut
                  : blocked
                    ? t.notAvailable
                    : t.add;
              return (
                <article key={product.id} className="grid gap-3 border-t p-4 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`compact-menu-product-${product.id}`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-extrabold" data-testid={`compact-menu-product-${product.id}-name`}>{product.name}</h4>
                      {product.campaign_badge?.label && (
                        <span data-testid={`compact-menu-product-${product.id}-campaign-badge`} className="store-badge-primary rounded-full px-2 py-0.5 text-xs font-bold">
                          {product.campaign_badge.label}
                        </span>
                      )}
                      {product.sold_out && (
                        <span data-testid={`compact-menu-product-${product.id}-sold-out-badge`} className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-700">
                          {t.soldOut}
                        </span>
                      )}
                    </div>
                    {description ? (
                      <p className="mt-1 text-sm text-muted-foreground" data-testid={`compact-menu-product-${product.id}-description`}>
                        {description}
                      </p>
                    ) : null}
                    <ProductBadges product={product} prefix={`compact-menu-product-${product.id}`} />
                    {showKitchenNotice ? (
                      <p className="mt-2 text-xs font-bold text-muted-foreground" data-testid={`compact-menu-product-${product.id}-kitchen-notice`}>
                        {t.kitchenCloseNotice}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <Money amount={product.price} currency={currency} testId={`compact-menu-product-${product.id}-price`} />
                    <Button
                      data-testid={`compact-menu-product-${product.id}-add-button`}
                      disabled={product.sold_out || orderingPaused || blocked}
                      size="sm"
                      className="store-btn-accent tap-target rounded-lg disabled:opacity-55"
                      onClick={() => addToCart(product)}
                    >
                      {buttonLabel}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )) : (
        <div className="p-6" data-testid="compact-menu-filter-empty-state">
          <h3 className="font-extrabold">No items match these filters.</h3>
          <p className="mt-1 text-sm text-muted-foreground">Clear filters or ask the business about alternatives.</p>
        </div>
      )}
    </div>
  );
}
function Info({ icon, title, body, testId }) { return <article className="rounded-lg border store-surface-card p-5 shadow-sm" data-testid={testId}><div className="store-text-primary [&_svg]:h-6 [&_svg]:w-6" aria-hidden="true">{icon}</div><h3 className="mt-3 font-extrabold" data-testid={`${testId}-title`}>{title}</h3><p className="mt-2 whitespace-pre-line text-sm leading-6 text-muted-foreground" data-testid={`${testId}-body`}>{body}</p></article>; }

function localizeDayLabel(daysLabel, t) {
  const map = {
    Mon: t.dayMon, Tue: t.dayTue, Wed: t.dayWed, Thu: t.dayThu,
    Fri: t.dayFri, Sat: t.daySat, Sun: t.daySun,
  };
  return String(daysLabel || "").replace(/\b(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\b/g, (day) => map[day] || day);
}

function HoursInfo({ rows, title, t = i18n.en, testId }) {
  const hasAnyOpen = rows.some((row) => row.open);
  return (
    <article className="rounded-lg border store-surface-card p-5 shadow-sm" data-testid={testId}>
      <div className="store-text-primary [&_svg]:h-6 [&_svg]:w-6" aria-hidden="true"><Clock /></div>
      <h3 className="mt-3 font-extrabold" data-testid={`${testId}-title`}>{title}</h3>
      {hasAnyOpen ? (
        <ul className="mt-3 space-y-1.5" data-testid={`${testId}-body`}>
          {rows.map((row) => (
            <li key={row.key} className="text-sm leading-6" data-testid={`${testId}-group-${row.days.join("-").toLowerCase()}`}>
              <span className="store-text-primary font-bold">{localizeDayLabel(row.daysLabel, t)}</span>
              <span className={row.open ? "text-muted-foreground" : "text-muted-foreground/70"}>
                {" "}{row.open ? row.label : t.hoursClosed}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm leading-6 text-muted-foreground" data-testid={`${testId}-body`}>{t.hoursSoon}</p>
      )}
    </article>
  );
}

function Checkout({ data, cart, updateCartQuantity, removeFromCart, clearCart, setOrder, navigate, t = i18n.en, language, setLanguage }) {
  const business = data.business;
  const [searchParams, setSearchParams] = useSearchParams();
  const pickupAvailable = business.pickup_enabled && business.store_operations?.pause_pickup !== true && business.store_operations?.is_open !== false && !business.temporary_closure;
  const deliveryAvailable = business.delivery_enabled && business.store_operations?.pause_delivery !== true && business.store_operations?.is_open !== false && !business.temporary_closure;
  const initialFulfillment = pickupAvailable ? "pickup" : deliveryAvailable ? "delivery" : "pickup";
  const [fulfillment, setFulfillment] = useState(initialFulfillment);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", postcode: "", requested_time: "ASAP", notes: "", marketing_consent: true });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const minOrderValue = Number(business.min_order_value || 0);
  const deliveryFee = fulfillment === "delivery" ? Number(business.delivery_fee || 0) : 0;
  const currencyCode = business.currency;
  const itemsSubtotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);
  const total = useMemo(() => itemsSubtotal + deliveryFee, [itemsSubtotal, deliveryFee]);
  // Delivery only: menu items must meet min (fee excluded). Pickup has no minimum.
  const belowDeliveryMinimum = fulfillment === "delivery" && minOrderValue > 0 && itemsSubtotal < minOrderValue;
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    trackAnalyticsEvent({
      event_type: "checkout_start",
      slug: business.slug,
      path: storePath(business.slug, '/checkout'),
    });
  }, [business.slug]);

  useEffect(() => {
    if (searchParams.get("canceled") !== "1") return undefined;
    const orderId = searchParams.get("order_id");
    let cancelled = false;
    (async () => {
      if (orderId) {
        try {
          await apiPost(`/storefront/${business.slug}/orders/${orderId}/abandon-payment`, {
            session_id: searchParams.get("session_id") || undefined,
          });
        } catch {
          /* order may already be removed */
        }
        try {
          window.sessionStorage.removeItem(`project_local_order_${orderId}`);
        } catch {
          /* ignore */
        }
      }
      if (cancelled) return;
      toast.message("Payment canceled — your cart is still here if you want to try again.");
      searchParams.delete("canceled");
      searchParams.delete("order_id");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.slug]);

  const placeOrder = async (event) => {
    event.preventDefault();
    setError("");
    if (!cart.length) { setError("Add an item before checkout."); return; }
    if (fulfillment === "pickup" && !pickupAvailable) { setError("Pickup is not available right now."); return; }
    if (fulfillment === "delivery" && !deliveryAvailable) { setError("Delivery is not available right now."); return; }
    if (belowDeliveryMinimum) { setError(`Minimum delivery order is ${formatMoney(minOrderValue, currencyCode)} (menu items, excluding fee).`); return; }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) { setError("Enter your name, email, and phone number."); return; }
    if (fulfillment === "delivery" && !form.address.trim()) { setError("Enter your delivery address."); return; }
    if (fulfillment === "delivery" && !form.city.trim()) { setError("Enter your city."); return; }
    if (fulfillment === "delivery" && !form.postcode.trim()) { setError("Enter your postcode."); return; }
    setSubmitting(true);
    try {
      const result = await apiPost(`/storefront/${business.slug}/checkout`, { customer: { name: form.name, email: form.email, phone: form.phone }, fulfillment_type: fulfillment, delivery_address: fulfillment === "delivery" ? { line1: form.address.trim(), city: form.city.trim(), postcode: form.postcode.trim() } : {}, marketing_consent: form.marketing_consent, requested_time: form.requested_time, notes: form.notes, payment_status: "payment_pending", items: cart.map(({ product_id, quantity }) => ({ product_id, quantity })) });
      setOrder(result);
      if (result.checkout_url) {
        try {
          window.sessionStorage.setItem(`project_local_order_${result.id}`, JSON.stringify(result));
        } catch {
          /* ignore */
        }
        // Keep cart until payment succeeds so cancel/back can retry.
        toast.message("Redirecting to secure card payment…");
        window.location.assign(result.checkout_url);
        return;
      }
      clearCart();
      toast.success("Order placed");
      navigate(storePath(business.slug, `/order/${result.id}`));
    } catch (err) {
      if (err.code === "ECONNABORTED") {
        setError("Your connection is slow. Please try again — your cart is saved.");
        return;
      }
      const detail = err.response?.data?.detail;
      setError(detail?.error?.message || detail || "We could not place the order. Please check your cart and try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return <main className="storefront-theme page-shell min-h-screen px-4 py-8" data-testid="checkout-page" style={resolveStoreTheme(business).cssVars}><form noValidate onSubmit={placeOrder} className="mx-auto grid max-w-5xl gap-6 md:grid-cols-[1fr_.8fr]" data-testid="checkout-form"><section className="rounded-2xl border bg-white p-6 shadow-sm"><div className="flex flex-wrap items-center justify-between gap-3"><Link data-testid="checkout-back-store-link" to={storePath(business.slug)} className="store-link text-sm font-bold">{t.back}</Link><LanguageToggle language={language} setLanguage={setLanguage} business={business} /></div><h1 className="mt-5 text-4xl font-extrabold" data-testid="checkout-title">{t.checkout}</h1>{error && <div className="mt-4 flex gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm font-bold text-orange-800" data-testid="checkout-error-alert"><AlertTriangle className="h-5 w-5" />{error}</div>}<div className="mt-6 grid gap-3 sm:grid-cols-2"><button type="button" disabled={!pickupAvailable} data-testid="checkout-pickup-button" onClick={() => pickupAvailable && setFulfillment("pickup")} className={`tap-target rounded-lg border p-4 text-left font-bold disabled:cursor-not-allowed disabled:opacity-45 ${fulfillment === "pickup" ? "store-chip-active" : "bg-white"}`}>{t.pickup}<span className="mt-1 block text-xs text-muted-foreground">{pickupAvailable ? business.pickup_instructions || "Collect from the counter" : "Pickup unavailable"}</span></button><button type="button" disabled={!deliveryAvailable} data-testid="checkout-delivery-button" onClick={() => deliveryAvailable && setFulfillment("delivery")} className={`tap-target rounded-lg border p-4 text-left font-bold disabled:cursor-not-allowed disabled:opacity-45 ${fulfillment === "delivery" ? "store-chip-active" : "bg-white"}`}>{t.delivery}<span className="mt-1 block text-xs text-muted-foreground">{deliveryAvailable ? `${business.delivery_time} · ${formatMoney(business.delivery_fee || 0, currencyCode)}` : "Delivery unavailable"}</span></button></div><div className="mt-6 grid gap-4"><Field label={t.name} testId="checkout-name-input" value={form.name} onChange={(value) => setField("name", value)} /><Field label={t.email} testId="checkout-email-input" type="email" value={form.email} onChange={(value) => setField("email", value)} /><Field label={t.phone} testId="checkout-phone-input" value={form.phone} onChange={(value) => setField("phone", value)} /><Field label={fulfillment === "delivery" ? "Preferred delivery time" : "Preferred pickup time"} testId="checkout-requested-time-input" value={form.requested_time} onChange={(value) => setField("requested_time", value)} />{fulfillment === "delivery" && <div className="grid gap-3 sm:grid-cols-2" data-testid="checkout-delivery-address-fields"><div className="sm:col-span-2"><Field label={t.deliveryAddress} testId="checkout-address-input" value={form.address} onChange={(value) => setField("address", value)} /></div><Field label={t.city || "City"} testId="checkout-city-input" value={form.city} onChange={(value) => setField("city", value)} /><Field label={t.postcode || "Postcode"} testId="checkout-postcode-input" value={form.postcode} onChange={(value) => setField("postcode", value)} /></div>}<Label data-testid="checkout-notes-label">{t.notes}</Label><Textarea data-testid="checkout-notes-textarea" value={form.notes} onChange={(event) => setField("notes", event.target.value)} placeholder="Allergies, gift notes, or collection details" /><label className="flex items-center gap-3 text-sm" data-testid="checkout-marketing-consent-label"><Checkbox data-testid="checkout-marketing-consent-checkbox" checked={form.marketing_consent} onCheckedChange={(checked) => setField("marketing_consent", Boolean(checked))} /> Send me offers from this business</label></div></section><aside className="rounded-2xl border bg-white p-6 shadow-sm" data-testid="checkout-summary"><h2 className="text-2xl font-extrabold" data-testid="checkout-summary-title">{t.orderReview}</h2><div className="mt-4 space-y-3">{cart.map(item => <div key={item.product_id} className="rounded-lg border p-3" data-testid={`checkout-item-${item.product_id}`}><div className="flex justify-between gap-3"><span className="font-bold" data-testid={`checkout-item-${item.product_id}-name`}>{item.name}</span><Money amount={item.price * item.quantity} currency={currencyCode} testId={`checkout-item-${item.product_id}-total`} /></div><div className="mt-3 flex items-center justify-between gap-2"><div className="flex items-center gap-2" data-testid={`checkout-item-${item.product_id}-quantity-controls`}><Button type="button" data-testid={`checkout-item-${item.product_id}-decrease-button`} variant="outline" size="icon" className="tap-target rounded-lg" onClick={() => item.quantity > 1 ? updateCartQuantity(item.product_id, item.quantity - 1) : removeFromCart(item.product_id)}><Minus className="h-4 w-4" /></Button><span className="w-8 text-center font-bold" data-testid={`checkout-item-${item.product_id}-quantity`}>{item.quantity}</span><Button type="button" data-testid={`checkout-item-${item.product_id}-increase-button`} variant="outline" size="icon" className="tap-target rounded-lg" onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)}><Plus className="h-4 w-4" /></Button></div><Button type="button" data-testid={`checkout-item-${item.product_id}-remove-button`} variant="ghost" size="icon" className="tap-target rounded-lg" onClick={() => removeFromCart(item.product_id)}><Trash2 className="h-4 w-4" /></Button></div></div>)}{!cart.length && <div className="store-surface-muted rounded-lg border p-4" data-testid="checkout-empty-cart-message"><p className="font-extrabold">{t.emptyCart}</p><p className="mt-1 text-sm text-muted-foreground">Add your first item from the menu.</p></div>}</div><div className="mt-6 border-t pt-4"><div className="flex justify-between text-sm"><span data-testid="checkout-delivery-fee-label">Delivery fee</span><Money amount={deliveryFee} currency={currencyCode} testId="checkout-delivery-fee" /></div>{fulfillment === "delivery" && minOrderValue > 0 && <><div className="mt-3 flex justify-between text-sm"><span data-testid="checkout-min-order-label">Delivery minimum</span><Money amount={minOrderValue} currency={currencyCode} testId="checkout-min-order-value" /></div>{belowDeliveryMinimum && <p className="mt-2 rounded-lg bg-orange-50 p-2 text-xs font-bold text-orange-800" data-testid="checkout-min-order-warning">Add {formatMoney(minOrderValue - itemsSubtotal, currencyCode)} more in menu items to checkout.</p>}</>}<div className="mt-3 flex justify-between text-xl font-extrabold"><span data-testid="checkout-total-label">{t.total}</span><Money amount={total} currency={currencyCode} testId="checkout-total" /></div><p className="store-surface-muted mt-3 rounded-lg p-3 text-xs text-muted-foreground" data-testid="checkout-payment-note">Secure online payment is completed before the bakery accepts your order.</p><Button data-testid="checkout-place-order-button" disabled={submitting || !cart.length || belowDeliveryMinimum || (fulfillment === "pickup" && !pickupAvailable) || (fulfillment === "delivery" && !deliveryAvailable)} className="store-btn-primary tap-target mt-5 w-full" type="submit">{submitting ? "Placing order…" : t.placeOrder}</Button></div></aside></form></main>;
}

function StatusPage({ order, setOrder, clearCart, slug, currency, t = i18n.en }) {
  const { orderId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resolved, setResolved] = useState(order || null);
  const [loading, setLoading] = useState(!order);
  const storeSlug = resolved?.business_slug || slug || "/";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const id = orderId || order?.id;
      if (!slug || !id) return;
      try {
        if (searchParams.get("paid") === "1") {
          const confirmed = await apiPost(`/storefront/${slug}/orders/${id}/confirm-payment`, {
            session_id: searchParams.get("session_id") || undefined,
          });
          if (cancelled) return;
          setResolved(confirmed);
          setOrder?.(confirmed);
          clearCart?.();
          toast.success("Payment confirmed");
          searchParams.delete("paid");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
        } else if (!order) {
          let cached = null;
          try {
            cached = JSON.parse(window.sessionStorage.getItem(`project_local_order_${id}`) || "null");
          } catch {
            cached = null;
          }
          if (cached?.id === id) {
            setResolved(cached);
            setOrder?.(cached);
          }
          const fetched = await apiGet(`/storefront/${slug}/orders/${id}`);
          if (cancelled) return;
          setResolved(fetched);
          setOrder?.(fetched);
          if (fetched?.payment_status === "paid") clearCart?.();
        }
      } catch (error) {
        if (!cancelled && !order) {
          toast.error(error.response?.data?.detail?.error?.message || "Could not load order");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, orderId]);

  if (loading && !resolved) return <LoadingScreen />;
  const display = resolved || order;
  return (
    <main className="page-shell grid min-h-screen place-items-center px-4 py-10" data-testid="order-status-page">
      <section className="w-full max-w-xl rounded-2xl border bg-white p-8 text-center shadow-xl" data-testid="order-status-card">
        <CheckCircle2 className="mx-auto h-12 w-12 text-accent" />
        <h1 className="mt-5 text-4xl font-extrabold" data-testid="order-confirmation-title">{t.confirmed}</h1>
        <p className="mt-3 text-muted-foreground" data-testid="order-confirmation-body">{t.confirmedBody}</p>
        {display && (
          <div className="mt-6 rounded-lg bg-[#F8F5EE] p-4">
            <p className="font-bold" data-testid="order-confirmation-number">{display.order_number}</p>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="order-confirmation-requested-time">
              Requested: {display.requested_time || "ASAP"}
            </p>
            <p className="mt-1 text-sm font-bold text-muted-foreground" data-testid="order-confirmation-payment">
              {display.payment_status === "paid" ? "Paid" : "Payment pending"}
            </p>
            <p data-testid="order-confirmation-total"><Money amount={display.total} currency={currency} /></p>
          </div>
        )}
        <Link data-testid="order-status-store-link" to={storePath(resolved?.business_slug || slug)}>
          <Button className="tap-target mt-6 rounded-lg">{t.back.replace("← ", "")}</Button>
        </Link>
      </section>
    </main>
  );
}

function OwnerPreviewBanner({ business }) {
  const needsPayment = business.subscription_status !== "active";
  const ctaPath = needsPayment ? "/onboarding/subscription" : "/onboarding/publish";
  const ctaLabel = needsPayment ? "Subscribe to go live" : "Finish go-live";
  return (
    <div className="border-b bg-amber-50 px-4 py-3 text-sm text-amber-950" data-testid="storefront-owner-preview-banner">
      <div className="pl-container flex flex-wrap items-center justify-between gap-3">
        <p className="font-bold" data-testid="storefront-owner-preview-text">
          Owner preview — customers cannot see this storefront until you subscribe and publish.
        </p>
        <Link data-testid="storefront-owner-preview-cta" to={ctaPath}>
          <Button size="sm" className="tap-target rounded-lg">{ctaLabel}</Button>
        </Link>
      </div>
    </div>
  );
}

function StoreUnavailable({ slug, error, business }) {
  const isNotLive = error?.code === "STORE_NOT_LIVE";
  const needsPayment = error?.subscriptionRequired || business?.subscription_status !== "active";
  const ownerMatch = business?.slug === slug;
  const ctaPath = needsPayment ? "/onboarding/subscription" : "/onboarding/publish";
  const title = isNotLive ? "Storefront not live yet" : "Store not found";
  const body = isNotLive
    ? (ownerMatch
      ? "Your storefront is saved as a draft. Subscribe and complete go-live to make it public for customers."
      : "This business has not finished setup and payment yet.")
    : (error?.message || "This BuildHof storefront does not exist.");
  return (
    <main className="page-shell grid min-h-screen place-items-center p-6" data-testid="store-not-found-page">
      <section className="max-w-lg rounded-lg border bg-white p-8 text-center">
        <h1 className="text-3xl font-extrabold" data-testid="store-not-found-title">{title}</h1>
        <p className="mt-3 text-muted-foreground" data-testid="store-not-found-body">{body}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          {isNotLive && ownerMatch && (
            <Link data-testid="store-not-live-subscribe-link" to={ctaPath}>
              <Button className="tap-target rounded-lg">{needsPayment ? "Subscribe to go live" : "Finish publishing"}</Button>
            </Link>
          )}
          {ownerMatch && (
            <Link data-testid="store-not-live-dashboard-link" to="/dashboard">
              <Button variant="outline" className="tap-target rounded-lg">Open dashboard</Button>
            </Link>
          )}
          <Link data-testid="store-not-found-home-link" to="/">
            <Button variant={isNotLive && ownerMatch ? "outline" : "default"} className="tap-target rounded-lg">Return home</Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
function Field({ label, testId, value, onChange, type = "text" }) { return <div><Label htmlFor={testId} data-testid={`${testId}-label`}>{label}</Label><Input id={testId} data-testid={testId} type={type} className="mt-2 h-12 rounded-lg" value={value} onChange={(event) => onChange?.(event.target.value)} /></div>; }